import React, { useCallback, useEffect, useState } from "react";
import { WorkingTimeClassicType } from "../../__generated__/graphql";

import TextField from "../TextField";
import { useI18n } from "../../hooks/i18n-hooks";

export type MonthlyPercentClassicComponentProps = {
    workingTime: WorkingTimeClassicType;
    onChange: (v: { workingTime: WorkingTimeClassicType; percentage?: number }) => void;
    disabled?: boolean;
    hoursPerWeek: number;
};

const MonthlyPercentClassicComponent: React.FC<MonthlyPercentClassicComponentProps> = ({
    workingTime,
    onChange,
    disabled = false,
    hoursPerWeek,
}) => {
    const i18n = useI18n();
    const buildPercentage = useCallback(
        (unitCount: number) => {
            const valueAsNumberPre = 100 * (unitCount / hoursPerWeek);
            const valueAsNumber = Math.round(valueAsNumberPre * 100) / 100;
            return valueAsNumber.toString();
        },
        [hoursPerWeek],
    );

    const [textValue, setTextValue] = useState(buildPercentage(workingTime.unitCount || 0));

    const onBlur = useCallback(() => {
        const factor = Number(Number(textValue.replace(",", ".")).toFixed(3)) / 100;
        if (!isNaN(factor)) {
            const valueAsNumberPre = hoursPerWeek * factor;
            const valueAsNumber = Math.round((valueAsNumberPre + Number.EPSILON) * 100) / 100;
            let changedValue: WorkingTimeClassicType | null;
            if (valueAsNumber <= 0 || valueAsNumber > 24 * 7) {
                changedValue = { ...workingTime, unitCount: undefined };
            } else {
                changedValue = { ...workingTime, unitCount: valueAsNumber };
            }
            onChange({ workingTime: changedValue });
        } else {
            onChange({ workingTime: { ...workingTime, unitCount: undefined } });
        }
    }, [hoursPerWeek, onChange, textValue, workingTime]);

    useEffect(() => {
        const percentage = buildPercentage(workingTime.unitCount || 0);
        setTextValue(percentage);
    }, [workingTime, buildPercentage]);

    return (
        <TextField
            style={{ flexShrink: 1, flexGrow: 0, width: "auto" }}
            label={i18n.t("utils.working_time.percent_label")}
            selectTextOnFocus
            inputStyle={{
                textAlign: "right",
                color: workingTime?.unitCount === undefined ? "red" : undefined,
            }}
            maxValue={100}
            disabled={disabled}
            value={textValue}
            isDecimalNumber
            onChangeText={setTextValue}
            onBlur={onBlur}
            keyboardType="decimal-pad"
            returnKeyType="done"
        />
    );
};

export default MonthlyPercentClassicComponent;
